import { TypeChecker } from '@attain-apps/type-guards'

import * as models from '../../models'

import type { Tracker } from './tracker'

const SEPARATOR = '±'

/**
 * @internal
 */
export class Maker<T, TEntity = T, TAliases = unknown> {
  public readonly aliases: TAliases

  private get value(): T {
    return this._params.extract(this._params.tracker.entity)
  }

  constructor(
    private readonly _params: Maker.constructor.Params<T, TEntity, TAliases>,
  ) {
    this.aliases = _params.aliases
  }

  public select({
    alias,
    collection,
    id,
    ids,
    update,
  }: {
    readonly alias?: ValidAlias<TAliases>
    readonly collection: models.CollectionProducer
  } & (
    | {
        readonly id: models.EntityId | undefined
        readonly ids?: undefined
        readonly update: (
          updatedEntity: T,
          updatedId: models.EntityId | undefined,
        ) => void
      }
    | {
        readonly id?: undefined
        readonly ids: readonly (models.EntityId | undefined)[]
        readonly update: (
          updatedEntity: T,
          updatedIds: readonly models.EntityId[],
        ) => void
      }
  )): void {
    // TODO: maybe move this somewhere else
    const selectionTarget = [
      this._params.tracker.collection.name,
      this._getCompleteAlias(alias),
    ]
      .filter(TypeChecker.isNotNullish.raw)
      .join(SEPARATOR)

    const finalIds = (ids ?? [id]).filter(TypeChecker.isNotNullish.raw)

    // Tracks association
    this._params.tracker.addAssociation({
      collection$: models.CollectionProducer.getEc$(collection, {
        waitUntilReady: true,
      }),
      id: `${selectionTarget}»${models.CollectionProducer.getName(collection)}`,
      selectionIds: finalIds,
      selectionTarget,
      // If we received IDs, the update function expects IDs. Otherwise, it
      // expects a single ID
      update: ids
        ? // NOTE: we create entirely different callbacks for performance
          (updatedEntity, updatedIds) =>
            update(this._params.extract(updatedEntity), updatedIds)
        : (updatedEntity, updatedIds) =>
            update(this._params.extract(updatedEntity), updatedIds[0]),
    })
  }

  public nest<TNested>({
    alias,
    extract,
    use,
  }: {
    readonly alias?: ValidAlias<TAliases>
    readonly extract: (entity: T) => TNested
    readonly use: (
      this: Maker<TNested, TEntity, TAliases>,
      val: TNested,
    ) => void
  }): void {
    use.call(
      new Maker({
        aliases: this.aliases,
        baseAlias: this._getCompleteAlias(alias),
        extract: entity => extract(this._params.extract(entity)),
        tracker: this._params.tracker,
      }),
      extract(this.value),
    )
  }

  private _getCompleteAlias(
    alias: ValidAlias<TAliases> | undefined,
  ): typeof alias {
    const completeAlias = [this._params.baseAlias, alias]
      .filter(TypeChecker.isNotNullish.raw)
      .join(SEPARATOR)
    // NOTE: casting has to do with deferred type parameters combined with our
    // safeguard type of ValidAlias
    return completeAlias.length === 0
      ? undefined
      : (completeAlias satisfies string as typeof alias)
  }
}
export namespace Maker {
  export namespace constructor {
    export interface Params<T, TEntity = T, TAliases = unknown> {
      readonly tracker: Tracker<any, TEntity>
      readonly extract: (entity: TEntity) => T
      readonly aliases: TAliases
      readonly baseAlias?: ValidAlias<TAliases>
    }
  }
}

/**
 * Gives us an error when trying to use an invalid alias
 */
type ValidAlias<TAliases> = [TAliases] extends [never]
  ? 'You must use the withAliases method in order to use aliases'
  : string
